import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface CertificateWidget {
  id: string;
  name: string;
  iframe_src: string;
  width: number;
  height: number;
  display_order: number;
  is_active: boolean;
}

interface CertificateWidgetManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  widget: CertificateWidget | null;
}

export function CertificateWidgetManagementDialog({ 
  open, 
  onOpenChange, 
  widget 
}: CertificateWidgetManagementDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    name: "",
    iframe_src: "",
    width: 300,
    height: 150,
    display_order: 0,
    is_active: true,
  });

  useEffect(() => {
    if (widget) {
      setFormData({
        name: widget.name,
        iframe_src: widget.iframe_src,
        width: widget.width,
        height: widget.height,
        display_order: widget.display_order,
        is_active: widget.is_active,
      });
    } else {
      setFormData({
        name: "",
        iframe_src: "",
        width: 300,
        height: 150,
        display_order: 0,
        is_active: true,
      });
    }
  }, [widget, open]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (widget) {
        const { error } = await supabase
          .from("certificate_widgets")
          .update(data)
          .eq("id", widget.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("certificate_widgets")
          .insert([data]);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-certificate-widgets"] });
      queryClient.invalidateQueries({ queryKey: ["certificate-widgets"] });
      toast({
        title: "Success",
        description: widget ? "Certificate widget updated successfully" : "Certificate widget added successfully",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clean iframe_src before saving (extract URL if full iframe code was entered)
    let cleanedIframeSrc = formData.iframe_src.trim();
    const srcMatch = cleanedIframeSrc.match(/src=["']([^"']+)["']/);
    if (srcMatch) {
      cleanedIframeSrc = srcMatch[1];
    }
    
    saveMutation.mutate({
      ...formData,
      iframe_src: cleanedIframeSrc
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {widget ? "Edit Certificate Widget" : "Add Certificate Widget"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Blockmark Tech Certificate 1"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="iframe_src">Iframe Source URL</Label>
            <Input
              id="iframe_src"
              value={formData.iframe_src}
              onChange={(e) => {
                let value = e.target.value.trim();
                // Auto-extract URL if user pastes full iframe code
                const srcMatch = value.match(/src=["']([^"']+)["']/);
                if (srcMatch) {
                  value = srcMatch[1];
                }
                setFormData({ ...formData, iframe_src: value });
              }}
              placeholder="https://registry.blockmarktech.com/certificates/..."
              required
            />
            <p className="text-sm text-muted-foreground">
              Enter only the URL (not the full iframe code). If you paste iframe code, the URL will be extracted automatically.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="width">Width (px)</Label>
              <Input
                id="width"
                type="number"
                value={formData.width}
                onChange={(e) => setFormData({ ...formData, width: parseInt(e.target.value) })}
                min={1}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="height">Height (px)</Label>
              <Input
                id="height"
                type="number"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: parseInt(e.target.value) })}
                min={1}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              min={0}
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}